import {
  AddressLookupTableAccount,
  Connection,
  PublicKey,
  SimulatedTransactionResponse,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import Decimal from 'decimal.js'
import { milliseconds } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import {
  JupiterV2BuildInstruction,
  JupiterV2BuildResponse,
  JupiterV6RouteInfo,
  SwapMode,
} from 'types/jupiter'
import {
  JUPITER_V6_QUOTE_API_MAINNET,
  QUOTE_ROUTES_ROUTING_MODE,
} from 'utils/constants'
import { jupiterFetch } from 'utils/jupiter'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { SpurClient } from 'spur-client'
import { getFeeEstimate } from 'utils/priorityFee'
import { makePythClient } from 'utils/makePythClient'
import { toContextTuplesString, toErrorOrStringify } from 'utils/logging'
import { useMemo } from 'react'
import { OKXQuote } from 'pages/api/okx'
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes'

function deserializeInstruction(
  ix: JupiterV2BuildInstruction,
): TransactionInstruction {
  return new TransactionInstruction({
    programId: new PublicKey(ix.programId),
    keys: ix.accounts.map((acc) => ({
      pubkey: new PublicKey(acc.pubkey),
      isSigner: acc.isSigner,
      isWritable: acc.isWritable,
    })),
    data: Buffer.from(ix.data, 'base64'),
  })
}

function buildTransactionFromV2Build(
  response: JupiterV2BuildResponse,
  payer: PublicKey,
): VersionedTransaction {
  const instructions: TransactionInstruction[] = [
    ...response.computeBudgetInstructions.map(deserializeInstruction),
    ...response.setupInstructions.map(deserializeInstruction),
    deserializeInstruction(response.swapInstruction),
    ...(response.cleanupInstruction
      ? [deserializeInstruction(response.cleanupInstruction)]
      : []),
    ...response.otherInstructions.map(deserializeInstruction),
  ]

  const lookupTables: AddressLookupTableAccount[] = Object.entries(
    response.addressesByLookupTableAddress || {},
  ).map(
    ([key, addresses]) =>
      new AddressLookupTableAccount({
        key: new PublicKey(key),
        state: {
          deactivationSlot: BigInt('18446744073709551615'),
          lastExtendedSlot: 0,
          lastExtendedSlotStartIndex: 0,
          authority: undefined,
          addresses: addresses.map((a) => new PublicKey(a)),
        },
      }),
  )

  const blockhash = bs58.encode(
    Buffer.from(response.blockhashWithMetadata.blockhash),
  )

  const messageV0 = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: blockhash,
    instructions,
  }).compileToV0Message(lookupTables)

  return new VersionedTransaction(messageV0)
}

export const ROUTER_INFO = {
  jupiter: { name: 'Jupiter', src: '/icons/jup-logo.svg' },
  dflow: { name: 'DFlow', src: '/icons/dflow-logo.svg' },
  wave: { name: 'Wave', src: '/icons/wave-logo.svg' },
  pyth: { name: 'Pyth Express Relay', src: '/icons/pyth-logo.svg' },
  okx: { name: 'OKX', src: '/icons/okx-logo.svg' },
} as const satisfies Record<
  NonNullable<JupiterV6RouteInfo['origin']>,
  { name: string; src: string }
>
export const ROUTER_INFO_LIST = Object.values(ROUTER_INFO)

enum EnabledRouter {
  ALL = 'ALL',
  JUPITER_DIRECT = 'JUPITER_DIRECT',
  JUPITER = 'JUPITER',
  DFLOW = 'DFLOW',
  WAVE = 'WAVE',
  PYTH = 'PYTH',
  OKX = 'OKX',
}

const ENABLED_ROUTERS = Object.values(EnabledRouter)

export function isEnabledRouter(value: string): value is EnabledRouter {
  return ENABLED_ROUTERS.includes(value)
}

const DEFAULT_TIMEOUT = milliseconds({ seconds: 3 })

const fetchJupiterRoute = async (
  wallet: string,
  inputMint: string,
  outputMint: string,
  amount: number,
  slippage = 0.5,
  maxAccounts = 64,
  timeout = DEFAULT_TIMEOUT,
): Promise<{ bestRoute: JupiterV6RouteInfo } | null> => {
  try {
    const jupiterUrl = new URL(`${JUPITER_V6_QUOTE_API_MAINNET}/build`)
    jupiterUrl.search = new URLSearchParams({
      taker: wallet,
      inputMint: inputMint,
      outputMint: outputMint,
      amount: String(amount),
      slippageBps: String(Math.ceil(slippage * 100)),
      maxAccounts: String(maxAccounts),
    }).toString()

    const response = await jupiterFetch(jupiterUrl, {
      signal: AbortSignal.timeout(timeout),
    })
    if (!response.ok) {
      const responseText = await response.text()
      const context = toContextTuplesString({
        status: response.status,
        response: responseText,
        inputMint,
        outputMint,
        amount,
        slippage,
        maxAccounts,
      })
      if (
        response.status === 400 &&
        (responseText.includes('COULD_NOT_FIND_ANY_ROUTE') ||
          responseText.includes('NO_ROUTES_FOUND') ||
          responseText.includes('TOKEN_NOT_TRADABLE'))
      ) {
        console.info(`No jupiter route found: ${context}`)
      } else {
        console.error(`Error fetching jupiter route: ${context}`)
      }
      return null
    }

    const buildResponse = (await response.json()) as JupiterV2BuildResponse
    const tx = buildTransactionFromV2Build(
      buildResponse,
      new PublicKey(wallet),
    )

    return {
      bestRoute: {
        inputMint: buildResponse.inputMint,
        inAmount: Number(buildResponse.inAmount),
        outputMint: buildResponse.outputMint,
        outAmount: Number(buildResponse.outAmount),
        otherAmountThreshold: Number(buildResponse.otherAmountThreshold),
        swapMode: SwapMode.ExactIn,
        slippageBps: buildResponse.slippageBps,
        priceImpactPct: 0,
        routePlan: buildResponse.routePlan.map((r) => ({
          swapInfo: {
            ammKey: r.swapInfo.ammKey,
            label: r.swapInfo.label,
            inputMint: r.swapInfo.inputMint,
            outputMint: r.swapInfo.outputMint,
            inAmount: Number(r.swapInfo.inAmount),
            outAmount: Number(r.swapInfo.outAmount),
            feeAmount: 0,
            feeMint: r.swapInfo.outputMint,
          },
          percent: r.percent,
        })),
        origin: 'jupiter' as const,
        tx,
      },
    }
  } catch (e) {
    console.error(
      `Error fetching jupiter route: ${toContextTuplesString({
        inputMint,
        outputMint,
        amount,
        slippage,
        maxAccounts,
      })}`,
      e,
    )
    return null
  }
}

export const fetchWaveRoute = async (
  connection: Connection,
  spurClient: SpurClient,
  inputMint: string,
  outputMint: string,
  amount: number,
  slippage = 0.5,
): Promise<{ bestRoute: JupiterV6RouteInfo } | null> => {
  try {
    const waveSlippage = slippage / 100
    const [quote, feeEstimate, blockhash] = await Promise.all([
      spurClient.quote(BigInt(amount), inputMint, outputMint, {
        encoded_bytes: 1200,
      }),
      getFeeEstimate(connection),
      connection.getLatestBlockhash('finalized'),
    ])
    const compiledSwap = await spurClient.compileSwap(
      connection,
      quote,
      waveSlippage,
      BigInt(feeEstimate),
      blockhash,
    )

    let simulationResult: SimulatedTransactionResponse | { err: unknown }
    try {
      simulationResult = (
        await connection.simulateTransaction(compiledSwap.swapTx, {
          sigVerify: false,
        })
      ).value
    } catch (e) {
      simulationResult = { err: e }
    }
    if (simulationResult.err) {
      const { logs, logLevel } =
        'logs' in simulationResult && simulationResult.logs
          ? simulationResult.logs.some((logLine) => {
              const lineLower = logLine.toLowerCase()
              return (
                lineLower.includes('insufficient funds') ||
                lineLower.includes('insufficient quote in atoms for swap') ||
                lineLower.includes('insufficient lamports')
              )
            })
            ? ({ logLevel: 'info', logs: simulationResult.logs } as const)
            : ({ logLevel: 'error', logs: simulationResult.logs } as const)
          : ({ logLevel: 'error', logs: [] as string[] } as const)

      // report simulation errors to spur
      if (logLevel === 'error' && logs.length > 0) {
        ;(
          compiledSwap as unknown as {
            report(payload: Record<string, unknown>): void
          }
        ).report({
          cabana: true,
          simulationError: simulationResult.err,
          simulationLogs: logs,
          swapTx: Buffer.from(compiledSwap.swapTx.serialize()).toString(
            'base64',
          ),
        })
      }

      console[logLevel](
        ...[
          `Internal simulation error for wave route: ${toContextTuplesString({
            inputMint,
            outputMint,
            amount,
            slippage,
          })}`,
          toErrorOrStringify(simulationResult.err),
          ...(logs.length > 0 ? [logs.join('\n')] : []),
        ],
      )
      return null
    }

    return {
      bestRoute: {
        origin: 'wave',
        inputMint: inputMint,
        inAmount: amount,
        outputMint: outputMint,
        outAmount: Number(quote.outputAmount),
        otherAmountThreshold: Number(quote.outputAmount),
        swapMode: SwapMode.ExactIn,
        slippageBps: Math.ceil(waveSlippage * 100),
        priceImpactPct: 0,
        tx: compiledSwap.swapTx,
        routePlan: quote.quotes.flatMap((quote) =>
          quote.swaps.map((swap) => ({
            swapInfo: {
              ammKey: swap.dex_id,
              label: swap.dex_name,
              inputMint: swap.swap_input_amount,
              outputMint: swap.swap_output_amount,
              inAmount: amount,
              outAmount: Number(swap.swap_output_amount),
              feeAmount: 0,
              feeMint: outputMint,
            },
            percent: 1,
          })),
        ),
      } satisfies JupiterV6RouteInfo,
    }
  } catch (e) {
    const context = toContextTuplesString({
      inputMint,
      outputMint,
      amount,
      slippage,
    })
    if (e instanceof Error && e.message.includes('could not find route')) {
      console.info(`No wave route found: ${context}`)
    } else {
      console.error(
        `Error fetching wave route: ${context}`,
        toErrorOrStringify(e),
      )
    }

    return null
  }
}

const fetchOKX = async (
  wallet: string,
  inputMint: string,
  outputMint: string,
  amount: number,
  slippage = 0.5,
  timeout = milliseconds({ seconds: 3 }),
): Promise<{ bestRoute: JupiterV6RouteInfo } | null> => {
  const url = new URL('/api/okx', window.location.origin)
  url.search = new URLSearchParams({
    wallet: wallet,
    fromTokenAddress: inputMint,
    toTokenAddress: outputMint,
    amount: String(amount),
    slippage: String(slippage / 100),
  }).toString()

  try {
    const response = await fetch(url, {
      signal: AbortSignal.timeout(timeout),
    })

    if (!response.ok) {
      const responseText = await response.text()
      // errors are logged in the API handler
      const logLevel = response.status === 500 ? 'info' : 'error'
      console[logLevel](
        `Error fetching okx route: ${toContextTuplesString({
          status: response.status,
          response: responseText,
          inputMint,
          outputMint,
          amount,
          slippage,
        })}`,
      )
      return null
    }

    const okxQuote = (await response.json()) as OKXQuote

    return {
      bestRoute: {
        origin: 'okx',
        inputMint: inputMint,
        inAmount: amount,
        outputMint: outputMint,
        outAmount: Number(okxQuote.routerResult.toTokenAmount),
        otherAmountThreshold: Number(okxQuote.routerResult.toTokenAmount),
        swapMode: SwapMode.ExactIn,
        slippageBps: Math.ceil(slippage * 100),
        priceImpactPct: 0,
        tx: VersionedTransaction.deserialize(bs58.decode(okxQuote.tx.data)),
        routePlan: [],
      } satisfies JupiterV6RouteInfo,
    }
  } catch (e) {
    console.error(
      `Error fetching okx route: ${toContextTuplesString({
        inputMint,
        outputMint,
        amount,
        slippage,
      })}`,
      e,
    )
    return null
  }
}

const fetchDFlowRoute = async (
  wallet: string,
  inputMint: string,
  outputMint: string,
  amount: number,
  slippage = 0.5,
  timeout = milliseconds({ seconds: 3 }),
): Promise<{ bestRoute: JupiterV6RouteInfo } | null> => {
  const url = new URL('/api/dflow', window.location.origin)
  url.search = new URLSearchParams({
    userPublicKey: wallet,
    inputMint: inputMint,
    outputMint: outputMint,
    amount: String(amount),
    slippageBps: String(Math.ceil(slippage * 100)),
  }).toString()

  try {
    const response = await fetch(url, {
      signal: AbortSignal.timeout(timeout),
    })
    if (!response.ok) {
      const responseText = await response.text()
      const context = toContextTuplesString({
        status: response.status,
        response: responseText,
        inputMint,
        outputMint,
        amount,
        slippage,
      })
      if (
        response.status === 400 &&
        (responseText.includes('route_not_found') ||
          responseText.includes('price_impact_too_high'))
      ) {
        console.info(`No dflow route found: ${context}`)
      } else {
        console.error(`Error fetching dflow route: ${context}`)
      }
      return null
    }

    const bestRoute = (await response.json()) as JupiterV6RouteInfo
    if (bestRoute.outAmount) {
      return {
        bestRoute: { ...bestRoute, origin: 'dflow' },
      }
    } else {
      console.error(
        `Error fetching dflow route: no route found: ${toContextTuplesString({
          response: JSON.stringify(bestRoute),
          inputMint,
          outputMint,
          amount,
          slippage,
        })}`,
      )
      return null
    }
  } catch (e) {
    console.error(
      `Error fetching dflow route: ${toContextTuplesString({
        inputMint,
        outputMint,
        amount,
        slippage,
      })}`,
      e,
    )
    return null
  }
}

const fetchPythRoute = async (
  wallet: string,
  inputMint: string,
  outputMint: string,
  amount: number,
  slippage = 0.5,
): Promise<{ bestRoute: JupiterV6RouteInfo } | null> => {
  try {
    const client = makePythClient()

    const quoteRequest = {
      chainId: 'solana',
      inputTokenMint: new PublicKey(inputMint),
      outputTokenMint: new PublicKey(outputMint),
      specifiedTokenAmount: {
        side: 'input' as const,
        amount: amount,
      },
      minimumLifetime: 35,
      userWallet: new PublicKey(wallet),
    }

    const quote = await client.getQuote(quoteRequest)
    if (!quote.transaction) {
      return null
    }
    const bestRoute: JupiterV6RouteInfo = {
      inAmount: Number(quote.inputToken.amount.toString()),
      outAmount: Number(quote.outputToken.amount.toString()),
      inputMint: inputMint,
      outputMint: outputMint,
      swapMode: 'ExactIn' as SwapMode,
      tx: quote.transaction,
      otherAmountThreshold: Number(quote.outputToken.amount.toString()),
      slippageBps: 0,
      priceImpactPct: 0,
      origin: 'pyth',
      referenceId: quote.referenceId,
    }
    return { bestRoute }
  } catch (e) {
    console.info('Error fetching pyth route:', e)
    return null
  }
}

export const handleGetRoutes = async (
  inputMint: string,
  inputMintProgram: string,
  outputMint: string,
  outputMintProgram: string,
  wallet: string | undefined,
  amount = 0,
  slippage = 0.5,
  swapMode = 'ExactIn',
  connection: Connection,
  waveClient?: SpurClient | undefined | null,
): Promise<{ bestRoute: JupiterV6RouteInfo | null }> => {
  if (amount <= 0) {
    return { bestRoute: null }
  }

  try {
    wallet ||= PublicKey.default.toBase58()

    const maxAccounts = 64

    const routes = [] as Promise<{ bestRoute: JupiterV6RouteInfo } | null>[]
    const allRoutersEnabled = enabledRouters.has(EnabledRouter.ALL)

    if (
      waveClient &&
      (allRoutersEnabled || enabledRouters.has(EnabledRouter.WAVE))
    ) {
      const waveRoute = withTimeout(
        fetchWaveRoute(
          connection,
          waveClient,
          inputMint,
          outputMint,
          amount,
          slippage,
        ),
        DEFAULT_TIMEOUT,
        'wave',
      )
      routes.push(waveRoute)
    }

    if (allRoutersEnabled || enabledRouters.has(EnabledRouter.DFLOW)) {
      const dflowRoute = fetchDFlowRoute(
        wallet,
        inputMint,
        outputMint,
        amount,
        slippage,
        DEFAULT_TIMEOUT,
      )
      routes.push(dflowRoute)
    }

    if (allRoutersEnabled || enabledRouters.has(EnabledRouter.OKX)) {
      //   const okx = fetchOKX(
      //     wallet,
      //     inputMint,
      //     outputMint,
      //     amount,
      //     slippage,
      //     DEFAULT_TIMEOUT,
      //   )
      //   routes.push(okx)
      //OKX
    }

    if (
      allRoutersEnabled ||
      enabledRouters.has(EnabledRouter.JUPITER) ||
      enabledRouters.has(EnabledRouter.JUPITER_DIRECT)
    ) {
      const jupiterRoute = fetchJupiterRoute(
        wallet,
        inputMint,
        outputMint,
        amount,
        slippage,
        maxAccounts,
        DEFAULT_TIMEOUT,
      )
      routes.push(jupiterRoute)
    }

    if (allRoutersEnabled || enabledRouters.has(EnabledRouter.PYTH)) {
      const pythRoute = withTimeout(
        fetchPythRoute(wallet, inputMint, outputMint, amount, slippage),
        DEFAULT_TIMEOUT,
        'pyth',
      )
      routes.push(pythRoute)
    }

    const responses = (await Promise.allSettled(routes)).flatMap((res) => {
      if (res.status === 'rejected') {
        console.error('Error fetching route', res.reason)
        return []
      }

      return res.value?.bestRoute != null ? [res.value] : []
    })

    const sortedByBiggestOutAmount = responses.sort((a, b) =>
      swapMode === 'ExactIn'
        ? Number(b.bestRoute.outAmount) - Number(a.bestRoute.outAmount)
        : Number(a.bestRoute.inAmount) - Number(b.bestRoute.inAmount),
    )
    return { bestRoute: sortedByBiggestOutAmount[0]?.bestRoute ?? null }
  } catch (e) {
    console.error('error fetching routes', e)
    return { bestRoute: null }
  }
}

const { valid: enabledRouters, invalid: invalidRouters } =
  QUOTE_ROUTES_ROUTING_MODE
    ? QUOTE_ROUTES_ROUTING_MODE.split(',').reduce(
        (acc, mode) => {
          if (isEnabledRouter(mode)) {
            acc.valid.add(mode)
          } else {
            acc.invalid.push(mode)
          }
          return acc
        },
        { valid: new Set<EnabledRouter>(), invalid: [] as string[] },
      )
    : { valid: new Set<EnabledRouter>(), invalid: [] as string[] }

if (invalidRouters.length > 0 || enabledRouters.size === 0) {
  throw new Error(
    `Invalid routers detected in QUOTE_ROUTES_ROUTING_MODE or no routers provided: ${toContextTuplesString(
      {
        valid: [...enabledRouters].join(','),
        invalid: invalidRouters.join(','),
        QUOTE_ROUTES_ROUTING_MODE,
      },
    )}`,
  )
}

interface UseQuoteRoutesPropTypes {
  inputMint: string
  inputMintProgram: string
  outputMint: string
  outputMintProgram: string
  nativeAmount: Decimal
  slippage: number
  swapMode: string
  refetchIntervalMs: number
  enabled?: boolean
}

const useQuoteRoutes = ({
  inputMint,
  inputMintProgram,
  outputMint,
  outputMintProgram,
  nativeAmount,
  slippage,
  swapMode,
  refetchIntervalMs,
  enabled,
}: UseQuoteRoutesPropTypes) => {
  const { publicKey } = useWallet()
  const { connection } = useConnection()
  const waveClient = useMemo(() => {
    if (!publicKey) return null
    return new SpurClient(publicKey.toBase58())
  }, [publicKey])

  const { data, isFetching, isLoading, isInitialLoading, refetch } = useQuery({
    queryKey: [
      'swap-routes',
      inputMint,
      outputMint,
      nativeAmount,
      slippage,
      swapMode,
      publicKey?.toBase58(),
    ],
    queryFn: async () =>
      handleGetRoutes(
        inputMint,
        inputMintProgram,
        outputMint,
        outputMintProgram,
        publicKey?.toBase58(),
        nativeAmount.toNumber(),
        slippage,
        swapMode,
        connection,
        waveClient,
      ),
    cacheTime: milliseconds({ seconds: 60 }),
    staleTime: milliseconds({ seconds: 3 }),
    refetchInterval: refetchIntervalMs,
    enabled: enabled ?? !!(nativeAmount.toNumber() && inputMint && outputMint),
    retry: 3,
  })

  return nativeAmount
    ? {
        ...(data ?? {
          routes: [],
          bestRoute: undefined,
        }),
        isFetching,
        isLoading,
        isInitialLoading,
        refetch,
      }
    : {
        routes: [],
        bestRoute: undefined,
        isFetching: false,
        isLoading: false,
        isInitialLoading: false,
        refetch: undefined,
      }
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timeout after ${ms}ms for ${label}`))
    }, ms)

    promise
      .then((value) => {
        clearTimeout(timer)
        resolve(value)
      })
      .catch((err) => {
        clearTimeout(timer)
        reject(err)
      })
  })
}

export default useQuoteRoutes
